/**
 * Entitlements Store
 *
 * Fetches and caches the role-based entitlements for the authenticated user.
 * The entitlements map drives RBAC across the app: route guards, sidebar
 * visibility, component-level v-entitlement directive, and the
 * useEntitlements() composable.
 *
 * Entitlements are fetched once after login and persisted in sessionStorage
 * so they survive page reloads and hard refreshes within the same tab.
 * When sessionStorage is empty (new tab, browser restart), the entitlementsGuard
 * automatically triggers a re-fetch on the first navigation for any authenticated user.
 * Call `reset()` on logout to clear state.
 */
import { defineStore } from 'pinia';
import i18next from 'i18next';
import type { EntitlementsResponse } from '@tms-phoenix/tms-types';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';

/** In-flight fetch mutex — prevents duplicate concurrent requests. */
let _fetchPromise: Promise<boolean> | null = null;
/** Incremented on reset() so any in-flight fetch started before the reset discards its results. */
let _generation = 0;

interface EntitlementsState {
  entitlements: Record<string, boolean>;
  planCode: string | null;
  planFlag: string | null;
  roleCode: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useEntitlementsStore = defineStore('entitlements', {
  state: (): EntitlementsState => ({
    entitlements: {},
    planCode: null,
    planFlag: null,
    roleCode: null,
    isLoaded: false,
    isLoading: false,
    error: null,
  }),

  getters: {
    /**
     * Check if a specific entitlement is granted.
     * Denies only when explicitly false.
     */
    hasEntitlement: (state) => (key: string): boolean => {
      return state.entitlements[key] !== false;
    },

    /**
     * Check if ALL of the given entitlements are granted.
     */
    hasAllEntitlements: (state) => (keys: string[]): boolean => {
      return keys.every((key) => state.entitlements[key] !== false);
    },

    /**
     * Check if ANY of the given entitlements is granted.
     */
    hasAnyEntitlement: (state) => (keys: string[]): boolean => {
      return keys.some((key) => state.entitlements[key] !== false);
    },
  },

  actions: {
    /**
     * Fetch entitlements from the backend.
     * Called once after successful login.
     *
     * Concurrent callers (e.g. fire-and-forget from _finishLogin racing with
     * entitlementsGuard) are deduplicated — they all await the same in-flight
     * request rather than one returning `false` prematurely and leaving the
     * guard thinking entitlements are unavailable.
     */
    async fetchEntitlements(): Promise<boolean> {
      if (this.isLoaded) return true;
      // Dedupe: return the in-flight promise so the entitlements guard can
      // await the real result instead of getting an immediate false.
      if (_fetchPromise) return _fetchPromise;

      this.isLoading = true;
      this.error = null;
      const generation = _generation;

      _fetchPromise = (async () => {
        try {
          const api = getApiClient();
          const response = await api.get<EntitlementsResponse>(
            `${API_ROUTES.ONBOARDING}/entitlement-matrix`,
          );

          // Discard results if reset() was called while this fetch was in-flight.
          if (_generation !== generation) return false;

          const data = response.data;
          this.entitlements = data.entitlements;
          this.planCode = data.plan_code;
          this.planFlag = data.plan_flag;
          this.roleCode = data.role_code;
          this.isLoaded = true;

          return true;
        } catch (err) {
          if (_generation !== generation) return false;
          this.error = err instanceof Error ? err.message : i18next.t('entitlements.fetchFailed');
          return false;
        } finally {
          this.isLoading = false;
          _fetchPromise = null;
        }
      })();

      return _fetchPromise;
    },

    /**
     * Reset entitlements state (call on logout).
     */
    reset() {
      _generation++;
      _fetchPromise = null;
      this.entitlements = {};
      this.planCode = null;
      this.planFlag = null;
      this.roleCode = null;
      this.isLoaded = false;
      this.isLoading = false;
      this.error = null;
    },
  },

  persist: {
    storage: sessionStorage,
  },
});
